import { HelpPopover } from "@/components/ui/help-popover";
import { ElementIcon, useElementIcon } from "@/components/ui/icons";
import { TreeHeightWrapper } from "@/components/ui/tree/tree-height-wrapper";
import {
  TREE_NODE_HEIGHT,
  TreeNode,
  TreeNodeProps,
} from "@/components/ui/tree/tree-node";
import { TreeData } from "@/utils/tree-generation/tree-data";
import {
  FaroText,
  FaroTooltip,
  grey,
  NoTranslate,
  TranslateVar,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { isMacOS, walkWithQueue } from "@faro-lotv/foundation";
import { CaptureTreeEntityType } from "@faro-lotv/service-wires";
import { Box } from "@mui/material";
import { Stack } from "@mui/system";
import { isEqual } from "es-toolkit";
import type { JSX } from "react";
import { useCallback, useEffect, useRef } from "react";
import { NodeApi, RowRendererProps, Tree, TreeApi } from "react-arborist";
import { TreeProps } from "react-arborist/dist/module/types/tree-props";
import { useDragDropManager } from "react-dnd";
import { useScansExportStore } from "./scans-export-store";
import { usePrepareScansExportData } from "./use-prepare-scans-export-data";

/** @returns The tree of scans within clusters which can be selected for export */
export function ScansExportTree({ height }: ScansExportTreeProps): JSX.Element {
  const { datasetSection } = usePrepareScansExportData();
  return (
    <Stack sx={{ gap: 2, width: "100%" }}>
      <FaroText variant="heading14" sx={{ px: 1.5, pt: 1 }}>
        <TranslateVar name="dataset name">{datasetSection.name}</TranslateVar>
      </FaroText>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, px: 1.5 }}>
        <FaroText variant="helpText">
          Select one or more scans from the list and adjust the export settings
          on the right
        </FaroText>
        <HelpPopover
          title="Selection Methods"
          variant="darkInfo"
          description={
            <Stack sx={{ gap: 1 }}>
              <FaroText variant="bodyS" color={grey[300]}>
                <b>Click:</b> Select or deselect a single scan{" "}
              </FaroText>
              <FaroText variant="bodyS" color={grey[300]}>
                <b>{isMacOS() ? "CMD" : "CTRL"} + Click:</b> Add or remove
                individual scans from the current selection
              </FaroText>
              <FaroText variant="bodyS" color={grey[300]}>
                <b>SHIFT + Click:</b> Select all scans between the first and
                last click
              </FaroText>{" "}
              <FaroText variant="bodyS" color={grey[300]}>
                <b>{isMacOS() ? "CMD" : "CTRL"} + Click on cluster:</b> Select
                or deselect all scans within the cluster
              </FaroText>
            </Stack>
          }
        />
      </Stack>
      <TreeHeightWrapper>
        <ScansExportTreeBase height={height} />
      </TreeHeightWrapper>
    </Stack>
  );
}

type ScansExportTreeProps = { height?: TreeProps<TreeData>["height"] };

/** @returns The tree of scans within clusters which can be selected for export */
function ScansExportTreeBase({ height }: ScansExportTreeProps): JSX.Element {
  const dragDropManager = useDragDropManager();
  const { clusterTree } = usePrepareScansExportData();
  const { selectedIds, multiSelectIds } = useScansExportStore();

  const treeRef = useRef<TreeApi<TreeData>>(null);

  // Sync the selection state with the tree
  useEffect(() => {
    const tree = treeRef.current;
    if (!tree) return;

    // Prevent infinite loop due to state sync
    if (isEqual(new Set(selectedIds), tree.selectedIds)) return;

    const lastSelected = selectedIds.at(-1);
    tree.focus(lastSelected ?? null);
    // Update the tree after focusing so that the list of visible nodes changes. Otherwise,
    // if the parent is collapsed, selecting a child does not work as expected because
    // the node is not the in the list of visible ones
    tree.update(tree.props);

    // Update the selection
    tree.setSelection({
      ids: selectedIds,
      anchor: selectedIds.at(0) ?? null,
      mostRecent: lastSelected ?? null,
    });
  }, [selectedIds, treeRef]);

  const handleNodesSelection = useCallback(
    (nodes: Array<NodeApi<TreeData>>): void => {
      // Check if the selected id is the scan or the cluster folder in the tree,
      // and get all the scan ids if it's a cluster folder
      function getAllLeafIds(node: NodeApi<TreeData>): string[] {
        if (!node.children || node.children.length === 0) {
          return [node.id];
        }
        return node.children.flatMap(getAllLeafIds);
      }

      const selectedIds = nodes.flatMap(getAllLeafIds);
      multiSelectIds(selectedIds);
    },
    [multiSelectIds],
  );

  return (
    <Tree<TreeData>
      dndManager={dragDropManager}
      ref={treeRef}
      data={clusterTree}
      disableDrag
      disableDrop
      width="100%"
      height={height}
      rowHeight={TREE_NODE_HEIGHT}
      indent={24}
      renderRow={ScanExportTreeRow}
      onSelect={handleNodesSelection}
    >
      {ScanExportTreeNode}
    </Tree>
  );
}

function ScanExportTreeRow({
  node,
  innerRef,
  attrs,
  children,
}: RowRendererProps<TreeData>): JSX.Element {
  const { invalidIds } = usePrepareScansExportData();

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      // Do not allow interaction with invalid clusters
      if (invalidIds.has(node.id)) return;

      // Ctrl/Cmd + click on a dataset (node with children)
      if (node.children && (e.ctrlKey || e.metaKey)) {
        // Gather all leaf nodes under this node, skipping invalid clusters
        const leafNodes: Array<NodeApi<TreeData>> = [];
        walkWithQueue([...node.children], (n, append) => {
          // Skip invalid clusters
          if (invalidIds.has(n.id)) return;
          if (n.children) {
            append(...n.children);
          } else {
            leafNodes.push(n);
          }
        });

        // Check if all leaf nodes are selected
        const allLeafSelected = leafNodes.every((leaf) => leaf.isSelected);

        // Select or deselect all leaf nodes
        leafNodes.forEach((leaf) => {
          if (allLeafSelected) {
            leaf.deselect();
          } else {
            leaf.selectMulti();
          }
        });
        return;
      }

      // Handle leaf nodes (no children) or other selection logic
      if (e.ctrlKey || e.metaKey) {
        if (node.isSelected) node.deselect();
        else node.selectMulti();
      } else if (e.shiftKey) {
        node.selectContiguous();
      } else if (node.isSelected) {
        node.deselect();
      } else {
        node.select();
      }

      // Deselect all datasets (nodes with children) and invalid clusters
      node.tree.selectedNodes.forEach((selectedNode) => {
        if (selectedNode.children || invalidIds.has(selectedNode.id)) {
          selectedNode.deselect();
        }
      });
    },
    [invalidIds, node],
  );

  const { hoverId, multiHoverIds, clearHovering } = useScansExportStore();
  const hoverNode = useCallback(() => {
    if (node.children) {
      multiHoverIds(node.children.map((child) => child.id));
    } else {
      hoverId(node.id);
    }
  }, [hoverId, multiHoverIds, node.children, node.id]);

  return (
    <Box
      component="div"
      ref={innerRef}
      {...attrs}
      onClick={onClick}
      onPointerEnter={hoverNode}
      onPointerLeave={clearHovering}
    >
      {children}
    </Box>
  );
}

function ScanExportTreeNode({
  node,
  style,
}: TreeNodeProps<TreeData>): JSX.Element {
  const icon = useElementIcon(node.data.element, node.data.directParent);
  const { invalidIds } = usePrepareScansExportData();

  // Disable scan and cluster nodes that are invalid for export
  const isDisabled = invalidIds.has(node.id);
  const isUnsupportedScan =
    isDisabled && node.data.element?.type !== CaptureTreeEntityType.cluster;

  return (
    <FaroTooltip
      title={
        isUnsupportedScan
          ? "This scan doesn't contain any data that can be exported."
          : ""
      }
      placement="right"
    >
      <Box component="div" style={{ ...style, height: "100%" }}>
        <TreeNode<TreeData> node={node} collapseIcon isDisabled={isDisabled}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              overflow: "hidden",
              width: "100%",
              gap: "8px",
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: "6px",
                maxWidth: "80%",
                flexGrow: 1,
              }}
            >
              <ElementIcon icon={icon} sx={{ fontSize: "1.125em" }} />
              <TruncatedFaroText
                variant="bodyM"
                tooltip={<NoTranslate>{node.data.label}</NoTranslate>}
                sx={{ color: node.isSelected ? grey[0] : grey[800] }}
              >
                {node.data.label}
              </TruncatedFaroText>
            </Stack>
          </Stack>
        </TreeNode>
      </Box>
    </FaroTooltip>
  );
}
